import CheckCircleIcon from "@redotech/redo-icons/check-circle_filled.svg";
import ReverseLeftIcon from "@redotech/redo-icons/reverse-left_filled.svg";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo } from "react";
import type { CompletedOperation } from "../hooks/use-intelligent-inventory";
import * as styles from "./completed-operation-card.module.css";

const FIELD_LABELS: Record<string, string> = {
  title: "title",
  sku: "SKU",
  price: "price",
  cost: "cost",
  vendor: "vendor",
  description: "description",
  weightGrams: "weight",
  upc: "UPC",
  tags: "Marketplace product tags",
  originCountry: "origin country",
  hsCode: "HS code",
  name: "name",
  street1: "street 1",
  street2: "street 2",
  city: "city",
  state: "state",
  postalCode: "postal code",
  country: "country",
  phoneNumber: "phone",
  website: "website",
  currency: "currency",
  leadTimeDays: "lead time",
  bufferDays: "buffer",
  minimumOrderQuantity: "minimum order quantity",
  reorderThreshold: "reorder threshold",
  forecastingEnabled: "forecasting",
  status: "status",
  poNumber: "PO number",
  reference: "reference",
  trackingUrl: "tracking URL",
  note: "note",
  noteToSupplier: "supplier note",
  products: "products",
};

function formatFieldList(fields: string[]): string {
  const labels = fields.map((f) => FIELD_LABELS[f] ?? f);
  if (labels.length === 1) {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    return labels[0]!;
  }
  if (labels.length === 2) {
    return `${labels[0]} and ${labels[1]}`;
  }
  return `${labels.slice(0, -1).join(", ")}, and ${labels[labels.length - 1]}`;
}

export function formatForecastingSummary(
  op: CompletedOperation,
  count: number,
  noun: string,
): string | null {
  if (
    !op.fields?.includes("forecastingEnabled") ||
    op.forecastingEnabled === undefined
  ) {
    return null;
  }

  const action = op.forecastingEnabled ? "Enabled" : "Disabled";
  const otherFields = op.fields.filter(
    (field) => field !== "forecastingEnabled",
  );
  if (otherFields.length === 0) {
    return `${action} forecasting for ${count} ${noun}`;
  }

  const formattedOtherFields = formatFieldList(otherFields);
  return `${action} forecasting and updated ${formattedOtherFields} for ${count} ${noun}`;
}

export function formatSummary(op: CompletedOperation): string {
  if (op.failed) {
    if (op.operationType === "create_transfer") {
      const count = op.transferCount ?? 1;
      const noun = count === 1 ? "transfer" : "transfers";
      return `Failed to create ${count} ${noun}`;
    }

    if (op.operationType === "submit_transfer") {
      const count = op.transferCount ?? 1;
      const noun = count === 1 ? "transfer" : "transfers";
      return `Failed to submit ${count} ${noun}`;
    }
  }

  if (op.operationType === "create_supplier") {
    const count = op.supplierCount ?? 1;
    return `Created ${count} ${count === 1 ? "supplier" : "suppliers"}`;
  }

  if (op.operationType === "update_supplier") {
    const count = op.supplierCount ?? 1;
    const noun = count === 1 ? "supplier" : "suppliers";
    if (op.fields && op.fields.length > 0) {
      return `Updated ${formatFieldList(op.fields)} for ${count} ${noun}`;
    }
    return `Updated ${count} ${noun}`;
  }

  if (op.operationType === "create_bom") {
    const bomCount = op.bomCount ?? 0;
    const bomNoun = bomCount === 1 ? "bill of materials" : "bills of materials";
    const productCount = op.productCount ?? 0;
    if (productCount > 0) {
      const productNoun = productCount === 1 ? "product" : "products";
      return `Created ${productCount} ${productNoun} and ${bomCount} ${bomNoun}`;
    }
    return `Created ${bomCount} ${bomNoun}`;
  }

  if (op.operationType === "update_bom") {
    const bomCount = op.bomCount ?? 1;
    const bomNoun = bomCount === 1 ? "bill of materials" : "bills of materials";
    if (op.fields && op.fields.length > 0) {
      return `Updated ${formatFieldList(op.fields)} for ${bomCount} ${bomNoun}`;
    }
    return `Updated ${bomCount} ${bomNoun}`;
  }

  if (op.operationType === "delete_bom") {
    const bomCount = op.bomCount ?? 1;
    const bomNoun = bomCount === 1 ? "bill of materials" : "bills of materials";
    return `Deleted ${bomCount} ${bomNoun}`;
  }

  if (op.operationType === "create_location") {
    const locCount = op.locationCount ?? 1;
    const locNoun = locCount === 1 ? "location" : "locations";
    return `Created ${locCount} ${locNoun}`;
  }

  if (op.operationType === "set_product_locations") {
    const count = op.productCount ?? 0;
    const noun = count === 1 ? "product" : "products";
    return `Updated locations for ${count} ${noun}`;
  }

  if (op.operationType === "create_purchase_order") {
    const poCount = op.purchaseOrderCount ?? 1;
    const poNoun = poCount === 1 ? "purchase order" : "purchase orders";
    return `Created ${poCount} ${poNoun}`;
  }

  if (op.operationType === "update_purchase_order") {
    const poCount = op.purchaseOrderCount ?? 1;
    const poNoun = poCount === 1 ? "purchase order" : "purchase orders";
    if (op.fields && op.fields.length > 0) {
      return `Updated ${formatFieldList(op.fields)} for ${poCount} ${poNoun}`;
    }
    return `Updated ${poCount} ${poNoun}`;
  }

  if (op.operationType === "adjust_inventory") {
    const count = op.productCount ?? 0;
    const noun = count === 1 ? "product" : "products";
    return `Updated inventory levels for ${count} ${noun}`;
  }

  if (op.operationType === "adjust_forecasts") {
    const adjustmentCount = op.forecastAdjustmentCount ?? 0;
    const adjustmentNoun =
      adjustmentCount === 1 ? "forecast adjustment" : "forecast adjustments";
    const skuCount = op.skuCount ?? 0;
    const skuNoun = skuCount === 1 ? "SKU" : "SKUs";
    return `Updated ${adjustmentCount} ${adjustmentNoun} for ${skuCount} ${skuNoun}`;
  }

  if (op.operationType === "create_transfer") {
    const count = op.transferCount ?? 1;
    const noun = count === 1 ? "transfer" : "transfers";
    return `Created ${count} ${noun}`;
  }

  if (op.operationType === "submit_transfer") {
    const count = op.transferCount ?? 1;
    const noun = count === 1 ? "transfer" : "transfers";
    return `Submitted ${count} ${noun}`;
  }

  const count = op.productCount ?? 0;
  const noun = count === 1 ? "product" : "products";

  if (op.operationType === "create") {
    return `Created ${count} ${noun}`;
  }

  if (op.operationType === "delete") {
    return `Deleted ${count} ${noun}`;
  }

  if (op.operationType === "link_suppliers") {
    const linksUpdated = op.linksUpdated ?? 0;
    if (linksUpdated > 0 && count <= linksUpdated) {
      const linkNoun =
        linksUpdated === 1 ? "product-supplier link" : "product-supplier links";
      return `Updated ${linksUpdated} ${linkNoun}`;
    }
    const supplierCount = op.supplierCount ?? 0;
    const supplierNoun = supplierCount === 1 ? "supplier" : "suppliers";
    if (linksUpdated > 0) {
      return `Linked ${count} ${noun} to ${supplierCount} ${supplierNoun}, updated ${linksUpdated} existing`;
    }
    return `Linked ${count} ${noun} to ${supplierCount} ${supplierNoun}`;
  }

  const forecastingSummary = formatForecastingSummary(op, count, noun);
  if (forecastingSummary) {
    return forecastingSummary;
  }

  if (op.fields && op.fields.length > 0) {
    return `Updated ${formatFieldList(op.fields)} for ${count} ${noun}`;
  }

  return `Updated ${count} ${noun}`;
}

export const CompletedOperationCard = memo(function CompletedOperationCard({
  operation,
  onUndo,
  isUndone,
}: {
  operation: CompletedOperation;
  onUndo?: () => void;
  isUndone?: boolean;
}) {
  return (
    <Flex align="center" className={styles.card} justify="space-between">
      <Flex align="center" gap="sm">
        {operation.failed ? (
          <Flex
            align="center"
            className={styles.failedIconBadge}
            justify="center"
          >
            <span aria-hidden="true" className={styles.failedIcon} />
          </Flex>
        ) : (
          <CheckCircleIcon className={`${styles.icon} ${styles.successIcon}`} />
        )}
        <Text
          fontSize="sm"
          fontWeight="medium"
          textColor={isUndone ? "secondary" : undefined}
          textDecoration={isUndone ? "line-through" : undefined}
        >
          {formatSummary(operation)}
        </Text>
      </Flex>
      {!isUndone &&
        onUndo &&
        !operation.failed &&
        operation.operationType !== "link_suppliers" &&
        operation.operationType !== "adjust_forecasts" &&
        operation.operationType !== "create_transfer" &&
        operation.operationType !== "submit_transfer" &&
        operation.operationType !== "delete_bom" &&
        !operation.adjustmentSessionKind?.includes("Count") && (
          <RedoButton
            hierarchy="secondary"
            IconLeading={ReverseLeftIcon}
            onClick={onUndo}
            size="sm"
            text="Undo changes"
            theme="normal"
          />
        )}
    </Flex>
  );
});
